import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import type { AuditEntityType } from '@byte-store-control/shared';
import type { Store } from '../../context/StoreManagementContext';

export type DateRangeOption = 'all' | 'today' | 'last7days' | 'last30days' | 'custom';

interface AuditFiltersProps {
  dateRange: DateRangeOption;
  onDateRangeChange: (range: DateRangeOption) => void;
  customStartDate?: string;
  customEndDate?: string;
  onCustomDateChange?: (start: string, end: string) => void;
  selectedStore?: string;
  onStoreChange?: (storeNumber: string) => void;
  selectedActionType?: AuditEntityType | '';
  onActionTypeChange?: (type: AuditEntityType | '') => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  stores?: Store[];
  showStoreFilter?: boolean;
  showActionTypeFilter?: boolean;
  showSearchFilter?: boolean;
}

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

const ACTION_TYPE_OPTIONS: { value: AuditEntityType | ''; label: string }[] = [
  { value: '', label: 'All Actions' },
  { value: 'store_hours', label: 'Store Hours' },
  { value: 'channel', label: 'Channels' },
  { value: 'item', label: 'Items' },
  { value: 'auth', label: 'Authentication' },
];

export const AuditFilters: React.FC<AuditFiltersProps> = ({
  dateRange,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomDateChange,
  selectedStore,
  onStoreChange,
  selectedActionType,
  onActionTypeChange,
  searchQuery,
  onSearchChange,
  stores = [],
  showStoreFilter = true,
  showActionTypeFilter = true,
  showSearchFilter = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} flexWrap="wrap" useFlexGap>
        {/* Date Range Selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => onDateRangeChange(e.target.value as DateRangeOption)}
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range */}
        {dateRange === 'custom' && onCustomDateChange && (
          <>
            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={customStartDate || ''}
              onChange={(e) => onCustomDateChange(e.target.value, customEndDate || '')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={customEndDate || ''}
              onChange={(e) => onCustomDateChange(customStartDate || '', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
          </>
        )}

        {/* Store Filter */}
        {showStoreFilter && onStoreChange && stores.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Store</InputLabel>
            <Select
              value={selectedStore || ''}
              label="Store"
              onChange={(e) => onStoreChange(e.target.value)}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.storeNumber} value={store.storeNumber}>
                  {store.storeNumber} - {store.storeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Action Type Filter */}
        {showActionTypeFilter && onActionTypeChange && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={selectedActionType || ''}
              label="Action Type"
              onChange={(e) => onActionTypeChange(e.target.value as AuditEntityType | '')}
            >
              {ACTION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Search */}
        {showSearchFilter && onSearchChange && (
          <TextField
            size="small"
            placeholder="Search by user..."
            value={searchQuery || ''}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, flexGrow: isMobile ? 1 : 0 }}
          />
        )}
      </Stack>
    </Box>
  );
};

/**
 * Calculate start and end dates based on date range option
 */
export function getDateRangeValues(dateRange: DateRangeOption): {
  startDate?: string;
  endDate?: string;
} {
  const now = new Date();
  const endDate = now.toISOString();

  switch (dateRange) {
    case 'all':
      // No date filtering - return undefined to fetch all logs
      return { startDate: undefined, endDate: undefined };
    case 'today': {
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      return { startDate: startOfToday.toISOString(), endDate };
    }
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start.toISOString(), endDate };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    default:
      return { startDate: undefined, endDate: undefined };
  }
}
